/**
 * Header for the Token Usage tab: the RANGED recorded-spend total beside the org's
 * LIFETIME pool counters, plus the honesty notes.
 *
 * The two kinds of number are not comparable and the labels say so. Recorded spend is
 * the sum of usage rows inside the selected window; pool balance and lifetime consumed
 * are running counters with no window at all. Putting them side by side without the
 * labels would invite exactly the subtraction that produces a wrong answer.
 */
import { SummaryCard } from '@/components/ui/summary-card';
import { formatExactTokens } from './tokenUsageView';

/**
 * Sourced from the org_scion token-burn attribution audit, 2026-07-27. The audit found
 * retained usage rows summing to MORE than lifetime pool consumption, because usage rows
 * and pool debits are written by different seams and some rows are record-only
 * (informational PATH rows, estimated / no-debit gateway rows). Stated as a standing
 * property rather than a per-org number, which is what it is.
 */
export const COVERAGE_NOTE =
  'Recorded spend and pool debits are written by different seams, so recorded spend can ' +
  'legitimately exceed pool debits — some rows are record-only (informational PATH rows, ' +
  'estimated or no-debit gateway rows). Read this tab as an attribution view, not a ' +
  'billing reconciliation. Source: token-burn attribution audit, 2026-07-27.';

/**
 * Voice practice calls run inside Parslee Core (VoiceSessionController), so their spend
 * is recorded under the Core source project. A reader comparing this tab against a
 * trainer-side figure will otherwise conclude trainer spend is missing.
 */
export const VOICE_ATTRIBUTION_NOTE =
  'Voice practice-call spend runs inside ButtonUp Core and is recorded under the Core ' +
  'source project, not SalesTrainer.';

export interface TokenUsageSummaryProps {
  /** Ranged recorded spend, or null while the source-project section is loading/failed. */
  rangedTotalTokens: number | null;
  /** Lifetime pool balance from the existing usage-summary endpoint. */
  poolBalance: number | null;
  /** Lifetime consumed from the existing usage-summary endpoint. */
  lifetimeConsumed: number | null;
  /** Set when the pool counters could not be read — the cards say so rather than showing 0. */
  poolError: string | null;
  /** Data-begins boundary note, when the window reaches back past retention. */
  dataBeginsNote: string | null;
}

function tokenValue(value: number | null, unavailable: string): string {
  return value === null ? unavailable : formatExactTokens(value);
}

export function TokenUsageSummary({
  rangedTotalTokens,
  poolBalance,
  lifetimeConsumed,
  poolError,
  dataBeginsNote,
}: TokenUsageSummaryProps) {
  const poolUnavailable = poolError ? 'Unavailable' : '—';

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          label="Recorded spend (selected range)"
          value={tokenValue(rangedTotalTokens, '—')}
          comparisonLabel="tokens"
          tooltipText="Sum of usage rows recorded inside the selected UTC window."
        />
        <SummaryCard
          label="Pool balance (lifetime)"
          value={tokenValue(poolBalance, poolUnavailable)}
          comparisonLabel="tokens"
          tooltipText="Running pool counter — not scoped to the selected range."
        />
        <SummaryCard
          label="Pool consumed (lifetime)"
          value={tokenValue(lifetimeConsumed, poolUnavailable)}
          comparisonLabel="tokens"
          tooltipText="Running pool counter — not scoped to the selected range."
        />
      </div>

      <p className="mt-2 text-xs text-muted-foreground">
        Pool balance and pool consumed are <strong>lifetime</strong> counters; recorded
        spend is scoped to the selected range. The two windows are not comparable.
      </p>

      {poolError && (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {poolError}
        </p>
      )}

      {dataBeginsNote && (
        <p className="mt-2 text-xs text-warning">{dataBeginsNote}</p>
      )}

      <p className="mt-2 text-xs text-muted-foreground">{COVERAGE_NOTE}</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {VOICE_ATTRIBUTION_NOTE}
      </p>
    </div>
  );
}
